import React, { ReactElement, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@companion-core/shared/app/Components/Button";
import {
  RequestSummaryButtonContainerProps,
  RequestSummaryButtonProps,
  UseRequestSummaryProps,
} from "@companion-core/shared/app/Components/Requests/RequestDetail/RequestSummary/requestSummary.model";

export const useRequestSummary = ({
  proposalId,
  recommendationId,
  performCancelRequest,
  performUpdateRequest,
  performDisplayProposals,
  performDisplayRecommendationDetail,
}: UseRequestSummaryProps) => {
  const { t } = useTranslation();

  const RequestSummaryCancelButton = ({ onVisited }: RequestSummaryButtonProps) => (
    <Button
      testID="request-detail__cancel-cta"
      classNames={["request-detail__footer-cta"]}
      type="secondary"
      size="large"
      label={t("request.my-requests.detail.button.cancel.label")}
      onClick={onVisited}
      onPress={onVisited}
    />
  );

  const RequestSummaryModifyButton = ({ status, onVisited }: RequestSummaryButtonProps) => (
    <Button
      testID="request-detail__modify-cta"
      classNames={["request-detail__modify-cta"]}
      type={status !== "recommended" ? "primary" : "secondary"}
      size="large"
      label={t("request.my-requests.detail.button.modify.label")}
      onClick={onVisited}
      onPress={onVisited}
    />
  );

  const RequestSummaryProposalsButton = ({ status, onVisited }: RequestSummaryButtonProps) => (
    <Button
      testID={`request-detail__${status === "proposal-rejected" ? "past-" : ""}proposals-cta`}
      classNames={["request-detail__proposals-cta"]}
      type="primary"
      size="large"
      label={t(
        `request.my-requests.detail.button.${status === "proposal-rejected" ? "previous-" : ""}proposals.label`
      )}
      onClick={onVisited}
      onPress={onVisited}
    />
  );

  const RequestSummarySelectedProposalButton = ({ onVisited }: RequestSummaryButtonProps) => (
    <Button
      testID="request-detail__selected-proposal-cta"
      classNames={["request-detail__proposals-cta"]}
      type="primary"
      size="large"
      label={t("request.my-requests.detail.button.selected-proposal.label")}
      onClick={onVisited}
      onPress={onVisited}
    />
  );

  /**
   * This is a mapping of the request status to the buttons that should be displayed
   * for that status. If a button is not defined for a status, it will not be displayed.
   */
  const buttonsByStatus: Record<string, ReactElement | null> = useMemo(
    () => ({
      "cancellation-requested": null,
      "modification-requested": null,
      "closed": null,
      "cancelled": null,
      "recommended": (
        <>
          <RequestSummaryModifyButton status="recommended" onVisited={performUpdateRequest} />
          <RequestSummaryProposalsButton status="recommended" onVisited={performDisplayProposals} />
        </>
      ),
      "recommended-by-mail": (
        <>
          <RequestSummaryCancelButton onVisited={performCancelRequest} />
          <RequestSummaryModifyButton onVisited={performUpdateRequest} />
        </>
      ),
      "proposal-validated": (
        <>
          <RequestSummaryModifyButton status="recommended" onVisited={performUpdateRequest} />
          <RequestSummarySelectedProposalButton
            onVisited={() =>
              performDisplayRecommendationDetail(proposalId ?? "", recommendationId ?? "")
            }
          />
        </>
      ),
      "proposal-rejected": (
        <RequestSummaryProposalsButton
          status="proposal-rejected"
          onVisited={performDisplayProposals}
        />
      ),
      "default": (
        <>
          <RequestSummaryCancelButton onVisited={performCancelRequest} />
          <RequestSummaryModifyButton onVisited={performUpdateRequest} />
        </>
      ),
    }),
    [
      performCancelRequest,
      performUpdateRequest,
      performDisplayProposals,
      performDisplayRecommendationDetail,
    ]
  );

  const RequestSummaryButtonContainer = ({
    status,
    hasDigitalProposal,
  }: RequestSummaryButtonContainerProps) => {
    // For a given status: if the buttonsByStatus is null, we don't return the default (we return null)
    if (buttonsByStatus[status] === undefined || (buttonsByStatus[status] && !hasDigitalProposal))
      return buttonsByStatus["default"];
    return buttonsByStatus[status];
  };

  return {
    RequestSummaryButtonContainer,
  };
};
